// src/app/modules/Technical/pages/Machinery/MachineryDetails.tsx
import { FC, useState, useEffect, useMemo } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { toast, ToastContainer } from 'react-toastify'
import { useAuth } from '../../../auth'
import { useGlobalFilter } from '../../../../shared/context/GlobalFilterContext'
import { useAccessibleVessels } from '../../../../shared/access/accessibleVessels'
import { getAccessibleVessels } from '../../core/technicalScopeUtils'
import { getVesselList, getCompanyList, getCompanyAdminList } from '../../../Management/core/_requests'
import {
  getEquipmentList,
  createEquipment,
  updateEquipment,
  deleteEquipment,
  getEquipmentLocationIcons,
  getSubcomponentsByEquipment,
  createSubcomponent,
  updateSubcomponent,
  deleteSubcomponent,
  getPartsBySubcomponent,
  createPartForSubcomponent,
  updatePart,
  deletePart,
  getVesselMachineryCounts,
  searchMachinery,
} from '../../core/_requests'
import { searchDefects } from '../../../QHSE/core/_requests'
import type {
  Vessel,
  EquipmentDto,
  EquipmentLocationIconDto,
  SubcomponentDto,
  PartDto,
  VesselMachineryCountsDto,
  MachinerySearchResult,
} from '../../core/_models'
import type { QHSEDefectDto } from '../../../QHSE/core/_models'
import { getPartById } from '../../../Procurement/core/_requests'
import { EquipmentModal } from './EquipmentModal'
import { SubcomponentModal } from './SubcomponentModal'
import PartModal from './PartModal'
import { MachineryUploadModal } from './MachineryUploadModal'
import PartAttachmentsModal from './PartAttachmentsModal'
import PartStorageLocationLibraryModal from '../../../Procurement/components/PartStorageLocationLibraryModal'
import GeneralItemModal from '../../../Procurement/components/GeneralItemModal'
import EquipmentIconModal from './EquipmentIconModal'
import { TECH_PERM } from '../../core/technicalPermissions'
import { TechnicalAccessDenied } from '../TechnicalAccessDenied'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type CompanyGroup = { id: number; name: string }
type Subcompany = { id: number; name: string; companyId: number }

type ExpandedState = {
  vessels: Set<number>
  equipment: Set<number>
  subcomponents: Set<number>
}

const MachineryDetails: FC = () => {
  const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '')
  const { currentUser, auth, roleDetails, isSuperAdmin, hasPermission } = useAuth()
  const { data: accessibleVessels } = useAccessibleVessels()
  const roleId = Number((auth?.userDetails as any)?.roleId ?? (currentUser?.role?.id ?? 0)) || 0
  const effectiveBehavior = (
    roleDetails?.effectiveBehaviorType ||
    (currentUser?.role as any)?.baseRoleType ||
    currentUser?.role?.roleName ||
    ''
  ).toUpperCase()
  const isCrew = roleId === 4 || effectiveBehavior === 'CREW'
  const operatorActsLikeSuperadmin = roleId === 6 && !currentUser?.companyGroupAdminId
  const canManageEquipmentIcons = isSuperAdmin || operatorActsLikeSuperadmin
  const canReadMachinery = hasPermission(TECH_PERM.READ_MACHINERY)
  const canCreateMachinery = hasPermission(TECH_PERM.CREATE_MACHINERY)
  const canUpdateMachinery = hasPermission(TECH_PERM.UPDATE_MACHINERY)
  const canDeleteMachinery = hasPermission(TECH_PERM.DELETE_MACHINERY)
  const canImportMachinery = hasPermission(TECH_PERM.IMPORT_MACHINERY)
  const canReadSubcomponent = hasPermission(TECH_PERM.READ_SUBCOMPONENT)
  const canCreateSubcomponent = hasPermission(TECH_PERM.CREATE_SUBCOMPONENT)
  const canUpdateSubcomponent = hasPermission(TECH_PERM.UPDATE_SUBCOMPONENT)
  const canDeleteSubcomponent = hasPermission(TECH_PERM.DELETE_SUBCOMPONENT)
  const canReadPart = hasPermission(TECH_PERM.READ_PART)
  const canCreatePart = hasPermission(TECH_PERM.CREATE_PART)
  const canUpdatePart = hasPermission(TECH_PERM.UPDATE_PART)
  const canDeletePart = hasPermission(TECH_PERM.DELETE_PART)
  const canReadTechnicalLocationLibrary = hasPermission(TECH_PERM.READ_TECHNICAL_LOCATION_LIBRARY)
  const canCreateTechnicalLocationLibrary = hasPermission(TECH_PERM.CREATE_TECHNICAL_LOCATION_LIBRARY)
  const canUpdateTechnicalLocationLibrary = hasPermission(TECH_PERM.UPDATE_TECHNICAL_LOCATION_LIBRARY)
  const canDeleteTechnicalLocationLibrary = hasPermission(TECH_PERM.DELETE_TECHNICAL_LOCATION_LIBRARY)
  const canReadGeneralItemLibrary = hasPermission(TECH_PERM.READ_TECHNICAL_GENERAL_ITEM_LIBRARY)
  const canCreateGeneralItemLibrary = hasPermission(TECH_PERM.CREATE_TECHNICAL_GENERAL_ITEM_LIBRARY)
  const canUpdateGeneralItemLibrary = hasPermission(TECH_PERM.UPDATE_TECHNICAL_GENERAL_ITEM_LIBRARY)
  const canDeleteGeneralItemLibrary = hasPermission(TECH_PERM.DELETE_TECHNICAL_GENERAL_ITEM_LIBRARY)
  const canImportGeneralItemLibrary = hasPermission(TECH_PERM.IMPORT_TECHNICAL_GENERAL_ITEM_LIBRARY)
  const canExportGeneralItemLibrary = hasPermission(TECH_PERM.EXPORT_TECHNICAL_GENERAL_ITEM_LIBRARY)

  const normalizeLocationCode = (value?: string | null) => {
    if (!value) return ''
    return value.trim().toUpperCase()
  }

  const resolveIconUrl = (path?: string | null) => {
    if (!path) return null
    if (/^https?:\/\//i.test(path)) return path
    if (path.startsWith('/uploads/')) return `${API_ORIGIN}${path}`
    return `${API_ORIGIN}/${path.replace(/^\/+/, '')}`
  }

  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [equipment, setEquipment] = useState<EquipmentDto[]>([])
  const [subcomponents, setSubcomponents] = useState<SubcomponentDto[]>([])
  const [parts, setParts] = useState<PartDto[]>([])
  const [loading, setLoading] = useState(false)
  const [vesselCounts, setVesselCounts] = useState<Map<number, VesselMachineryCountsDto>>(new Map())
  const [searchResults, setSearchResults] = useState<MachinerySearchResult | null>(null)
  const [searching, setSearching] = useState(false)
  const [defectEquipmentIds, setDefectEquipmentIds] = useState<Set<number>>(new Set())
  const [defectSubcomponentIds, setDefectSubcomponentIds] = useState<Set<number>>(new Set())
  const [equipmentIcons, setEquipmentIcons] = useState<Record<string, EquipmentLocationIconDto>>({})

  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
  })

  // Sync global header filters → local filters
  useEffect(() => {
    const gvId = globalState.vessel?.id
    if (gvId != null) {
      setFilters(prev => ({ ...prev, vesselId: String(gvId) }))
    } else if (globalState.company) {
      const allowedIds = new Set(globalVessels.map((v: any) => String(v.id)))
      if (filters.vesselId && !allowedIds.has(filters.vesselId)) {
        const first = globalVessels[0]
        setFilters(prev => ({ ...prev, vesselId: first ? String(first.id) : '' }))
      }
    } else {
      if (vesselsScoped.length > 0 && !filters.vesselId && isCrew) {
        setFilters(prev => ({ ...prev, vesselId: String(vesselsScoped[0].id) }))
      }
    }
  }, [globalState.vessel, globalState.company, globalVessels, vesselsScoped])

  const [searchTerm, setSearchTerm] = useState('')
  const [partListMode, setPartListMode] = useState<'all' | 'criticalOnly' | 'criticalFirst'>('all')
  const [expanded, setExpanded] = useState<ExpandedState>({
    vessels: new Set(),
    equipment: new Set(),
    subcomponents: new Set(),
  })

  // Modals
  const [equipmentModal, setEquipmentModal] = useState<{ open: boolean; editing?: EquipmentDto }>({ open: false })
  const [subcomponentModal, setSubcomponentModal] = useState<{ open: boolean; editing?: SubcomponentDto; equipmentId?: number }>({ open: false })
  const [partModal, setPartModal] = useState<{ open: boolean; editing?: PartDto; subcomponentId?: number; context?: any }>({ open: false })
  const [uploadModal, setUploadModal] = useState<boolean>(false)
  const [partAttachmentsModal, setPartAttachmentsModal] = useState<{ open: boolean; part?: PartDto | null }>({ open: false, part: null })
  // const [viewPartModal, setViewPartModal] = useState<{ open: boolean; part?: InventoryPart | null }>({ open: false, part: null })
  const [storageLibraryOpen, setStorageLibraryOpen] = useState(false)
  const [showGeneralItemModal, setShowGeneralItemModal] = useState(false)
  const [iconModal, setIconModal] = useState<{ open: boolean; locationCode?: string }>({ open: false })
  const [viewDetail, setViewDetail] = useState<{ open: boolean; kind?: 'equipment' | 'subcomponent' | 'part'; data?: any }>({
    open: false,
  })

  const isSearchMode = searchTerm.trim().length > 0 && !!searchResults

  // ─────────────────────────────────────────────
  // Initial lookups
  // ─────────────────────────────────────────────
  useEffect(() => {
    loadLookups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleVessels, roleDetails])

  useEffect(() => {
    if (vesselsScoped.length > 0) {
      loadEquipment()
      loadVesselCounts()
    } else {
      setEquipment([])
      setSubcomponents([])
      setParts([])
      setVesselCounts(new Map())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselsScoped])

  // Reload equipment when vessel filter changes
  useEffect(() => {
    if (filters.vesselId) {
      loadEquipment()
    } else {
      // Clear data when no vessel is selected
      setEquipment([])
      setSubcomponents([])
      setParts([])
    }
    // Load vessel counts when filter changes (especially for "All Vessels")
    loadVesselCounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vesselId, filters.companyId, filters.subcompanyId])

  useEffect(() => {
    const vesselIdNum = filters.vesselId ? Number(filters.vesselId) : undefined
    if (!vesselIdNum || !Number.isFinite(vesselIdNum)) {
      setDefectEquipmentIds(new Set())
      setDefectSubcomponentIds(new Set())
      return
    }

    const loadDefectBadges = async () => {
      try {
        const defects = await searchDefects({ vesselId: vesselIdNum, activeOnly: true })
        const equipmentSet = new Set<number>()
        const subSet = new Set<number>()
        defects.forEach((d: QHSEDefectDto) => {
          if (d.status === 'CLOSE' || d.status === 'REVIEWED') return
          if (d.equipmentId) equipmentSet.add(Number(d.equipmentId))
          const subIds = (d.subComponentIds && d.subComponentIds.length > 0)
            ? d.subComponentIds
            : d.subComponentId
              ? [d.subComponentId]
              : []
          subIds.forEach(id => {
            if (id != null) subSet.add(Number(id))
          })
        })
        setDefectEquipmentIds(equipmentSet)
        setDefectSubcomponentIds(subSet)
      } catch (error) {
        console.error('Failed to load defect badges:', error)
        setDefectEquipmentIds(new Set())
        setDefectSubcomponentIds(new Set())
      }
    }

    loadDefectBadges()
  }, [filters.vesselId])

  useEffect(() => {
    const term = searchTerm.trim()
    const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined

    if (!term || !vesselId) {
      setSearchResults(null)
      setSearching(false)
      return
    }

    const handle = window.setTimeout(() => {
      setSearching(true)
      searchMachinery(vesselId, term)
        .then((data) => {
          setSearchResults(data)
        })
        .catch((error) => {
          console.error('Error searching machinery:', error)
          setSearchResults({ equipment: [], subcomponents: [], parts: [] })
        })
        .finally(() => setSearching(false))
    }, 300)

    return () => window.clearTimeout(handle)
  }, [searchTerm, filters.vesselId])

  useEffect(() => {
    if (!isSearchMode || !searchResults) return

    const eqSet = new Set<number>()
    const subSet = new Set<number>()
    searchResults.equipment?.forEach((eq) => {
      if (eq.id) eqSet.add(eq.id)
    })
    searchResults.subcomponents?.forEach((sub) => {
      if (sub.id) subSet.add(sub.id)
    })

    setExpanded(prev => ({
      vessels: filters.vesselId ? new Set([Number(filters.vesselId)]) : prev.vessels,
      equipment: eqSet,
      subcomponents: subSet,
    }))
  }, [isSearchMode, searchResults, filters.vesselId])

  const loadLookups = async () => {
    try {
      const [groupsRaw, adminsRaw, vesselList, iconList] = await Promise.all([
        getCompanyAdminList?.().catch(() => []),
        getCompanyList?.().catch(() => []),
        getVesselList().catch(() => [] as Vessel[]),
        getEquipmentLocationIcons().catch(() => []),
      ])

      const groups: CompanyGroup[] = Array.isArray(groupsRaw)
        ? groupsRaw.map((g: any) => ({
          id: Number(g.id),
          name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
        }))
        : []

      const admins: Subcompany[] = Array.isArray(adminsRaw)
        ? adminsRaw.map((a: any) => ({
          id: Number(a.id),
          name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
          companyId: Number(
            a.cgaid?.id ??
            a.cga?.id ??
            a.companyGroupAdminId ??
            a.companyGroupId
          ),
        }))
        : []

      setSubcompanies(admins.filter(a => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

      const iconMap: Record<string, EquipmentLocationIconDto> = {}
      if (Array.isArray(iconList)) {
        iconList.forEach((icon) => {
          const key = normalizeLocationCode(icon.locationCode)
          if (key) {
            iconMap[key] = {
              ...icon,
              iconUrl: resolveIconUrl(icon.iconUrl) || '',
            }
          }
        })
      }
      setEquipmentIcons(iconMap)

      // Scope vessels for current user using authoritative helper (supports custom roles and assigned vessels)
      const vesselsForUser = getAccessibleVessels(
        vesselList,
        currentUser,
        isSuperAdmin,
        roleDetails,
        accessibleVessels
      )
      if (isCrew && currentUser?.vessel?.id) {
        setFilters(prev => ({ ...prev, vesselId: String(currentUser.vessel.id) }))
      }

      setVesselsScoped(vesselsForUser)

      // Auto-select first vessel for office roles
      if (vesselsForUser.length > 0 && !filters.vesselId && !isCrew) {
        const firstVessel = vesselsForUser[0]
        const v: any = firstVessel
        const companyGroupId = v.companyGroupAdmin?.id ?? v.companyGroupId ?? v.cgaid?.id ?? null
        const companyAdminId = v.companyAdmin?.id ?? v.companyId ?? null
        setFilters({
          companyId: companyGroupId ? String(companyGroupId) : '',
          subcompanyId: companyAdminId ? String(companyAdminId) : '',
          vesselId: String(firstVessel.id),
        })
      }
    } catch (error) {
      console.error('Error loading lookups:', error)
      toast.error('Failed to load lookups')
    }
  }

  // ─────────────────────────────────────────────
  // Data loaders (keep logic as-is)
  // ─────────────────────────────────────────────
  const loadEquipment = async () => {
    setLoading(true)
    try {
      // Filter equipment by selected vessel if vesselId is set
      const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
      if (!vesselId) {
        setEquipment([])
        setSubcomponents([])
        setParts([])
        return
      }
      const data = await getEquipmentList(vesselId)
      setEquipment(data)
      // Lazy load subcomponents and parts on expand
      setSubcomponents([])
      setParts([])
    } catch (error) {
      console.error('Error loading equipment:', error)
      toast.error('Failed to load equipment')
    } finally {
      setLoading(false)
    }
  }

  const loadSubcomponentsForEquipment = async (equipmentId: number) => {
    try {
      if (isSearchMode) return
      const subs = await getSubcomponentsByEquipment(equipmentId, false)
      setSubcomponents(prev => {
        const existing = prev.filter(s => s.equipmentId !== equipmentId)
        return [...existing, ...subs]
      })
    } catch (error) {
      console.error('Error loading subcomponents:', error)
    }
  }

  const loadPartsForSubcomponent = async (subcomponentId: number) => {
    if (isSearchMode) return
    const partList = await getPartsBySubcomponent(subcomponentId)
    setParts(prev => {
      const remaining = prev.filter(p => p.subcomponent?.id !== subcomponentId && p.subcomponentId !== subcomponentId)
      return [...remaining, ...partList]
    })
  }

  const loadVesselCounts = async () => {
    try {
      const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
      const counts = await getVesselMachineryCounts(vesselId)
      const countsMap = new Map<number, VesselMachineryCountsDto>()
      counts.forEach(count => {
        countsMap.set(count.vesselId, count)
      })
      setVesselCounts(countsMap)
    } catch (error) {
      console.error('Error loading vessel counts:', error)
      // Don't show error toast as this is a background operation
    }
  }

  // ─────────────────────────────────────────────
  // Expand / collapse handlers
  // ─────────────────────────────────────────────
  const handleToggleVessel = (vesselId: number) => {
    setExpanded(prev => {
      const vesselsSet = new Set(prev.vessels)
      vesselsSet.has(vesselId) ? vesselsSet.delete(vesselId) : vesselsSet.add(vesselId)
      return { ...prev, vessels: vesselsSet }
    })
    setFilters(prev => ({ ...prev, vesselId: String(vesselId) }))
  }

  const handleToggleEquipment = (equipmentId: number) => {
    setExpanded(prev => {
      const eqSet = new Set(prev.equipment)
      if (eqSet.has(equipmentId)) {
        eqSet.delete(equipmentId)
      } else {
        eqSet.add(equipmentId)
        // ensure fresh subcomponents
        if (!isSearchMode) {
          loadSubcomponentsForEquipment(equipmentId)
        }
      }
      return { ...prev, equipment: eqSet }
    })
  }

  const handleToggleSubcomponent = (sub: SubcomponentDto) => {
    const subKey = getSubcomponentKey(sub)
    const baseSubcomponentId = sub.id
    if (!subKey) return
    setExpanded(prev => {
      const subSet = new Set(prev.subcomponents)
      if (subSet.has(subKey)) {
        subSet.delete(subKey)
      } else {
        subSet.add(subKey)
        // ensure fresh parts (shared across instances)
        if (baseSubcomponentId && !isSearchMode) {
          loadPartsForSubcomponent(baseSubcomponentId)
        }
      }
      return { ...prev, subcomponents: subSet }
    })
  }

  // ─────────────────────────────────────────────
  // CRUD handlers (same logic, cleaned a bit)
  // ─────────────────────────────────────────────
  const handleCreateEquipment = async (data: EquipmentDto) => {
    if (!canCreateMachinery) {
      toast.error('You do not have permission to create machinery')
      return
    }
    try {
      await createEquipment(data)
      toast.success('Equipment created successfully')
      setEquipmentModal({ open: false })
      await loadEquipment()
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to create equipment')
    }
  }

  const handleUpdateEquipment = async (id: number, data: EquipmentDto) => {
    if (!canUpdateMachinery) {
      toast.error('You do not have permission to update machinery')
      return
    }
    try {
      await updateEquipment(id, data)
      toast.success('Equipment updated successfully')
      setEquipmentModal({ open: false })
      await loadEquipment()
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to update equipment')
    }
  }

  const handleDeleteEquipment = async (id: number) => {
    if (!canDeleteMachinery) {
      toast.error('You do not have permission to delete machinery')
      return
    }
    if (!window.confirm('Delete this equipment? This will also delete all subcomponents and parts.')) return
    try {
      await deleteEquipment(id)
      toast.success('Equipment deleted successfully')
      await loadEquipment()
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to delete equipment')
    }
  }

  const handleCreateSubcomponent = async (data: SubcomponentDto) => {
    if (!canCreateSubcomponent) {
      toast.error('You do not have permission to create subcomponents')
      return
    }
    try {
      await createSubcomponent(data)
      toast.success('Subcomponent created successfully')
      setSubcomponentModal({ open: false })
      if (data.equipmentId) {
        await loadSubcomponentsForEquipment(data.equipmentId)
      }
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to create subcomponent')
    }
  }

  const handleUpdateSubcomponent = async (id: number, data: SubcomponentDto) => {
    if (!canUpdateSubcomponent) {
      toast.error('You do not have permission to update subcomponents')
      return
    }
    try {
      await updateSubcomponent(id, data)
      toast.success('Subcomponent updated successfully')
      setSubcomponentModal({ open: false })
      if (data.equipmentId) {
        await loadSubcomponentsForEquipment(data.equipmentId)
      }
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to update subcomponent')
    }
  }

  const handleDeleteSubcomponent = async (id: number) => {
    if (!canDeleteSubcomponent) {
      toast.error('You do not have permission to delete subcomponents')
      return
    }
    if (!window.confirm('Delete this subcomponent?')) return
    try {
      await deleteSubcomponent(id)
      toast.success('Subcomponent deleted successfully')
      setSubcomponents(prev => prev.filter(s => s.id !== id))
      setParts(prev => prev.filter(p => p.subcomponent?.id !== id && p.subcomponentId !== id))
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to delete subcomponent')
    }
  }

  const handleCreatePart = async (data: PartDto) => {
    if (!canCreatePart) {
      toast.error('You do not have permission to create parts')
      return
    }
    if (!data.subcomponentId) return
    try {
      await createPartForSubcomponent({
        subcomponentId: data.subcomponentId,
        name: data.name,
        code: data.code,
        location: data.location,
        uomId: data.uom?.id ?? (data as any)?.uomId,
        unitOfMeasure: data.unitOfMeasure,
        robQuantity: data.robQuantity,
        minThreshold: data.minThreshold,
        maxThreshold: data.maxThreshold,
        rob: data.rob,
        isSpare: data.isSpare,
        criticalSpare: data.criticalSpare,
        manufacturer: data.manufacturer,
        partNo: data.partNo,
        serialNumber: data.serialNumber,
        vesselId: data.vesselId,
        equipmentId: (data as any).equipmentId,
      })
      await loadPartsForSubcomponent(data.subcomponentId)
      setPartModal({ open: false })
      toast.success('Part added successfully')
    } catch (error: any) {
      console.error('Error creating part', error)
      toast.error('Failed to add part')
    }
  }

  const handleUpdatePart = async (id: number, data: PartDto) => {
    if (!canUpdatePart) {
      toast.error('You do not have permission to update parts')
      return
    }
    try {
      await updatePart(id, {
        ...data,
        rob: data.rob,
        maxThreshold: data.maxThreshold,
        manufacturer: data.manufacturer,
        partNo: data.partNo,
        serialNumber: data.serialNumber,
        isSpare: data.isSpare,
        criticalSpare: data.criticalSpare,
        location: data.location,
        code: data.code,
        name: data.name,
        subcomponentId: data.subcomponentId,
        vesselId: data.vesselId,
        equipmentId: data.equipmentId,
      })
      if (data.subcomponentId) {
        await loadPartsForSubcomponent(data.subcomponentId)
      }
      setPartModal({ open: false })
      toast.success('Part updated successfully')
    } catch (error: any) {
      console.error('Error updating part', error)
      toast.error('Failed to update part')
    }
  }

  const handleDeletePart = async (part: PartDto) => {
    if (!canDeletePart) {
      toast.error('You do not have permission to delete parts')
      return
    }
    if (!part?.id) return
    if (!window.confirm('Delete this part?')) return

    try {
      await deletePart(part.id)
      setParts((prev) => prev.filter((p) => p.id !== part.id))
      toast.success('Part deleted successfully')
    } catch (error: any) {
      console.error('Error deleting part', error)
      const message = error?.message || ''
      const usedPartMessage = 'This part is being used somewhere, so it cannot be deleted directly.'
      if (message.toLowerCase().includes('being used somewhere')) {
        toast.error(usedPartMessage)
        return
      }
      toast.error('Failed to delete part')
    }
  }

  const handleViewPart = async (partId?: number) => {
    if (!canReadPart) {
      toast.error('You do not have permission to view parts')
      return
    }
    if (!partId) return
    try {
      const part = await getPartById(partId)
      setViewDetail({ open: true, kind: 'part', data: part })
    } catch (error) {
      console.error('Error loading part details:', error)
      toast.error('Failed to load part details')
    }
  }

  const openEditPartModal = async (
    part: PartDto,
    subcomponentId: number | undefined,
    context: {
      companyName: string
      vesselName: string
      equipmentName: string
      subcomponentName: string
      vesselId: number | undefined
      equipmentId: number | undefined
    }
  ) => {
    if (!canUpdatePart) {
      toast.error('You do not have permission to update parts')
      return
    }
    let editing: PartDto = part
    if (part.id) {
      try {
        const fullPart = await getPartById(part.id)
        editing = {
          ...part,
          ...(fullPart as any),
          rob:
            (fullPart as any)?.robQuantity ??
            (fullPart as any)?.rob ??
            (part as any)?.robQuantity ??
            (part as any)?.rob,
        } as PartDto
      } catch (error) {
        console.warn('Failed to fetch full part details before edit, using list row data:', error)
      }
    }

    setPartModal({
      open: true,
      editing,
      subcomponentId,
      context,
    })
  }

  const compareLocationCode = (a?: string | null, b?: string | null) => {
    const left = (a ?? '').trim()
    const right = (b ?? '').trim()
    if (!left && !right) return 0
    if (!left) return 1
    if (!right) return -1

    const toSegments = (value: string) =>
      value.split('.').map(seg => {
        const num = Number(seg)
        return Number.isFinite(num) ? num : seg
      })

    const leftSegs = toSegments(left)
    const rightSegs = toSegments(right)
    const maxLen = Math.max(leftSegs.length, rightSegs.length)

    for (let i = 0; i < maxLen; i += 1) {
      const l = leftSegs[i]
      const r = rightSegs[i]
      if (l === undefined) return -1
      if (r === undefined) return 1
      if (typeof l === 'number' && typeof r === 'number') {
        if (l !== r) return l - r
      } else {
        const lStr = String(l)
        const rStr = String(r)
        if (lStr !== rStr) return lStr.localeCompare(rStr)
      }
    }
    return left.localeCompare(right)
  }

  // ─────────────────────────────────────────────
  // Derived collections
  // ─────────────────────────────────────────────
  const displayEquipment = useMemo(
    () => (isSearchMode ? searchResults?.equipment ?? [] : equipment),
    [isSearchMode, searchResults, equipment]
  )

  const displaySubcomponents = useMemo(
    () => (isSearchMode ? searchResults?.subcomponents ?? [] : subcomponents),
    [isSearchMode, searchResults, subcomponents]
  )

  const displayParts = useMemo(
    () => (isSearchMode ? searchResults?.parts ?? [] : parts),
    [isSearchMode, searchResults, parts]
  )

  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (allowedVesselIds != null) {
      list = list.filter((v: any) => allowedVesselIds.has(Number(v.id)))
    }
    return list
  }, [vesselsScoped, allowedVesselIds])

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  const filteredVessels = useMemo(() => {
    let list = vesselsForFilters

    if (filters.vesselId) {
      const vId = Number(filters.vesselId)
      list = list.filter(v => v.id === vId)
    }

    if (searchTerm) {
      if (isSearchMode) {
        list = list.filter(v => displayEquipment.some(e => e.vesselId === v.id))
      } else {
        const term = searchTerm.toLowerCase()
        list = list.filter(v =>
          (v.fleet_name || '').toLowerCase().includes(term) ||
          displayEquipment.some(e => e.vesselId === v.id && (
            e.name.toLowerCase().includes(term) ||
            (e.code && e.code.toLowerCase().includes(term))
          ))
        )
      }
    }
    return list
  }, [vesselsForFilters, displayEquipment, searchTerm, filters.vesselId, isSearchMode])

  const subcomponentsForEquipment = (equipmentId: number) =>
    displaySubcomponents
      .filter(s => s.equipmentId === equipmentId)
      .slice()
      .sort((a, b) => {
        const nameA = (a.subComponentFunctionDescription || a.name || '').trim().toLowerCase()
        const nameB = (b.subComponentFunctionDescription || b.name || '').trim().toLowerCase()
        if (nameA && nameB) {
          const byName = nameA.localeCompare(nameB)
          if (byName !== 0) return byName
        } else if (nameA || nameB) {
          return nameA ? -1 : 1
        }
        const codeA = (a.subComponentCode || '').trim().toLowerCase()
        const codeB = (b.subComponentCode || '').trim().toLowerCase()
        if (codeA && codeB) {
          const byCode = codeA.localeCompare(codeB)
          if (byCode !== 0) return byCode
        } else if (codeA || codeB) {
          return codeA ? -1 : 1
        }
        const idA = a.id ?? 0
        const idB = b.id ?? 0
        return idA - idB
      })

  const getSubcomponentKey = (sub: SubcomponentDto) =>
    sub.id ?? 0

  const getSubcomponentDisplayName = (sub: SubcomponentDto) =>
    sub.subComponentFunctionDescription || sub.name

  const valueOrDash = (val: any) => {
    if (val === null || val === undefined) return '-'
    if (typeof val === 'string' && val.trim() === '') return '-'
    return String(val)
  }
  const formatYesNo = (val: any) => (val === true ? 'Yes' : val === false ? 'No' : '-')
  const formatStorageLocation = (val?: string | null) => {
    if (!val) return '-'
    const parts = val
      .split(':')
      .map((part) => part.trim())
      .filter(Boolean)
    if (parts.length === 0) return '-'
    return parts.join(': ')
  }

  const isCriticalSparePart = (part: PartDto) => {
    const rawValue =
      part.criticalSpare ??
      (part as any)?.critical_spare ??
      (part as any)?.criticalSparePart ??
      (part as any)?.isCriticalSpare

    if (typeof rawValue === 'boolean') return rawValue
    if (typeof rawValue === 'number') return rawValue === 1
    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase()
      return ['true', 'yes', 'y', '1'].includes(normalized)
    }

    return false
  }

  // subcomponents are now under equipment directly

  const sortPartsForDisplay = (list: PartDto[]) =>
    list
      .slice()
      .sort((a, b) => {
        if (partListMode === 'criticalFirst') {
          const criticalDiff = Number(isCriticalSparePart(b)) - Number(isCriticalSparePart(a))
          if (criticalDiff !== 0) return criticalDiff
        }

        const nameA = (a.name || '').trim().toLowerCase()
        const nameB = (b.name || '').trim().toLowerCase()
        if (nameA && nameB) {
          const byName = nameA.localeCompare(nameB)
          if (byName !== 0) return byName
        } else if (nameA || nameB) {
          return nameA ? -1 : 1
        }

        const codeA = (a.itemCode || a.code || '').trim().toLowerCase()
        const codeB = (b.itemCode || b.code || '').trim().toLowerCase()
        if (codeA && codeB) {
          const byCode = codeA.localeCompare(codeB)
          if (byCode !== 0) return byCode
        } else if (codeA || codeB) {
          return codeA ? -1 : 1
        }

        return (a.id ?? 0) - (b.id ?? 0)
      })

  const partsForSubcomponent = (subcomponentId: number) => {
    const linkedParts = displayParts.filter(
      (p) => p.subcomponent?.id === subcomponentId || p.subcomponentId === subcomponentId
    )

    const filteredParts =
      partListMode === 'criticalOnly'
        ? linkedParts.filter((part) => isCriticalSparePart(part))
        : linkedParts

    return sortPartsForDisplay(filteredParts)
  }

  const isCriticalSubcomponentForPartsView = (subcomponent: SubcomponentDto) => {
    if (subcomponent.criticality) return true
    if (!subcomponent.id) return false

    return displayParts.some(
      (part) =>
        isCriticalSparePart(part) &&
        (part.subcomponent?.id === subcomponent.id || part.subcomponentId === subcomponent.id)
    )
  }

  const criticalPartCountForEquipment = (equipmentId: number) =>
    subcomponentsForEquipment(equipmentId).filter((subcomponent) =>
      isCriticalSubcomponentForPartsView(subcomponent)
    ).length

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  if (!canReadMachinery) {
    return (
      <TechnicalAccessDenied
        title='Machinery Management'
        message='You do not have permission to view machinery.'
      />
    )
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-light'>
          <div className='card shadow-lg border-0'>
            <div className='card-header border-0 pt-6 pb-4 d-flex flex-wrap justify-content-between align-items-start bg-white gap-3'>
              <div>
                <h3 className='card-title mb-2 text-dark fw-bold fs-3'>
                  <span className='text-primary me-2'>⚙️</span>
                  Machinery Management
                </h3>
                <span className='text-muted fs-7 fw-normal'>
                  Hierarchical view of vessel equipment, subcomponents & parts
                </span>
              </div>
              <div className='d-flex align-items-center gap-2'>
                {canImportMachinery && (
                  <button
                    type='button'
                    className='btn btn-sm btn-success fw-semibold shadow-sm'
                    onClick={() => setUploadModal(true)}
                    title='Bulk upload equipment, subcomponents, and parts from Excel/CSV'
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = ''
                    }}
                  >
                    <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-2 me-2' />
                    Bulk Upload
                  </button>
                )}
                {filters.vesselId && canCreateMachinery && (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary fw-semibold shadow-sm'
                    onClick={() => setEquipmentModal({ open: true })}
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = ''
                    }}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2 me-2' />
                    Add Equipment
                  </button>
                )}
                {filters.vesselId && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light fw-semibold shadow-sm'
                    onClick={() => setViewDetail({ open: true, kind: 'equipment', data: null })}
                    style={{ display: 'none' }}
                  >
                    {/* placeholder for alignment */}
                  </button>
                )}
                {canReadTechnicalLocationLibrary && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light fw-semibold shadow-sm'
                    onClick={() => setStorageLibraryOpen(true)}
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = ''
                    }}
                  >
                    <KTSVG path='/media/map/warehouse.svg' className='svg-icon-2 me-2' />
                    Storage Location Library
                  </button>
                )}
                {canReadGeneralItemLibrary && (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary fw-semibold shadow-sm'
                    onClick={() => setShowGeneralItemModal(true)}
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = ''
                    }}
                  >
                    <KTSVG path='/media/map/library.svg' className='svg-icon-2 me-2' />
                    General Items Library
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-5 bg-light border-top'>
              <div className='row gx-3 gy-3 mb-4 align-items-end'>

                {/* Search */}
                <div className='col-md-4 col-lg-4'>
                  <label className='form-label fw-semibold fs-7 text-gray-700 mb-2'>
                    <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-3 me-1' />
                    Search
                  </label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-2' />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      placeholder='Search equipment, subcomponents, parts...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {searching && (
                    <div className='text-muted fs-8 mt-1'>Searching...</div>
                  )}
                </div>

                <div className='col-md-2 d-flex justify-content-md-end'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger w-100 w-md-auto fw-semibold shadow-sm'
                    onClick={() => {
                      setFilters({ companyId: '', subcompanyId: '', vesselId: '' })
                      setSearchTerm('')
                      setExpanded({ vessels: new Set(), equipment: new Set(), subcomponents: new Set() })
                    }}
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2 me-1' />
                    Clear All
                  </button>
                </div>
              </div>

              {/* Tree section */}
              {loading ? (
                <div className='text-center py-8'>
                  <div className='spinner-border text-primary' role='status' style={{ width: '3rem', height: '3rem' }}>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  <div className='mt-3 text-muted fs-6'>Loading machinery data...</div>
                </div>
              ) : filteredVessels.length === 0 ? (
                <div className='text-center py-10'>
                  <KTSVG path='/media/icons/duotune/general/gen025.svg' className='svg-icon-5x text-gray-400 mb-4' />
                  <div className='text-muted fs-5 fw-semibold mb-2'>
                    {filters.vesselId
                      ? 'No equipment found'
                      : 'Select filters to view machinery'}
                  </div>
                  <div className='text-muted fs-7'>
                    {filters.vesselId
                      ? 'No equipment found for selected filters. Try adjusting your search criteria.'
                      : 'Please select a vessel or adjust your filters to view the machinery tree.'}
                  </div>
                </div>
              ) : (
                <div
                  className='machinery-tree border-0 rounded-3 p-3 bg-white shadow-sm'
                  style={{ maxHeight: '70vh', overflowY: 'auto', transition: 'all 0.3s ease', fontSize: '0.9rem' }}
                >
                  {filteredVessels.map(vessel => {
                    const isExpandedVessel = expanded.vessels.has(vessel.id)
                    const vesselEquipment = displayEquipment
                      .filter(e => e.vesselId === vessel.id)
                      .slice()
                      .sort((a, b) => {
                        const locCompare = compareLocationCode(a.location, b.location)
                        if (locCompare !== 0) return locCompare
                        return (a.name || '').localeCompare(b.name || '')
                      })
                    const isSelectedVessel = filters.vesselId === String(vessel.id)
                    const vesselCount = vesselCounts.get(vessel.id)
                    const equipmentCount = vesselCount?.equipmentCount ?? vesselEquipment.length

                    return (
                      <div key={vessel.id} className='mb-4'>
                        {/* Vessel Row */}
                        <div
                          className={`d-flex align-items-center p-3 rounded-3 border shadow-sm ${isSelectedVessel
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white border-gray-300'
                            }`}
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          }}
                          onClick={() => handleToggleVessel(vessel.id)}
                          onMouseEnter={(e) => {
                            if (!isSelectedVessel) {
                              e.currentTarget.style.transform = 'translateX(4px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
                              e.currentTarget.style.borderColor = '#0d6efd'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelectedVessel) {
                              e.currentTarget.style.transform = 'translateX(0)'
                              e.currentTarget.style.boxShadow = ''
                              e.currentTarget.style.borderColor = ''
                            }
                          }}
                        >
                          <KTSVG
                            path={`/media/icons/duotune/arrows/arr${isExpandedVessel ? '072' : '071'}.svg`}
                            className={`svg-icon-4 me-3 ${isSelectedVessel ? 'text-white' : 'text-primary'}`}
                          />
                          <div className={`rounded-circle p-2 ${isSelectedVessel ? 'bg-white bg-opacity-20' : 'bg-light-primary'}`}>
                            <KTSVG
                              path='/media/icons/duotune/general/gen025.svg'
                              className={`svg-icon-4 ${isSelectedVessel ? 'text-white' : 'text-primary'}`}
                            />
                          </div>
                          <div className='flex-grow-1 ms-2'>
                            <div className={`fw-bold fs-6 ${isSelectedVessel ? 'text-white' : 'text-dark'}`}>
                              {vessel.fleet_name || `Vessel #${vessel.id}`}
                            </div>
                            <div className={`fs-7 mt-1 ${isSelectedVessel ? 'text-white text-opacity-75' : 'text-muted'}`}>
                              <span className={`badge me-2 ${isSelectedVessel ? 'bg-white bg-opacity-20' : 'bg-light-primary text-primary'}`}>
                                {equipmentCount}
                              </span>
                              equipment record{equipmentCount !== 1 ? 's' : ''}
                              {vesselCount && (
                                <span className={`ms-2 ${isSelectedVessel ? 'text-white text-opacity-75' : 'text-muted'}`}>
                                  • {vesselCount.subcomponentCount} subcomponents • {vesselCount.partCount} parts
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Equipment & below */}
                        {isExpandedVessel && (
                          <div className='ms-6 mt-3 position-relative'>
                            {/* Connecting line */}
                            <div
                              className='position-absolute'
                              style={{
                                left: '-12px',
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                background: 'linear-gradient(to bottom, #0d6efd, #e0e0e0)',
                                borderRadius: '2px'
                              }}
                            />
                            {vesselEquipment.length === 0 ? (
                              <div className='text-muted p-4 fs-7 bg-light rounded-3 border border-dashed'>
                                <KTSVG path='/media/icons/duotune/technology/teh005.svg' className='svg-icon-3x text-gray-400 mb-2' />
                                <div>
                                  No equipment found.
                                  {canCreateMachinery && (
                                    <> Use <span className='fw-semibold text-primary'>Add Equipment</span> to create one.</>
                                  )}
                                </div>
                              </div>
                            ) : (
                              vesselEquipment.map((eq) => {
                                const eqId = eq.id!
                                const isEqExpanded = expanded.equipment.has(eqId)
                                const eqSubcomponents = subcomponentsForEquipment(eqId)
                                const visibleEqSubcomponents =
                                  partListMode === 'criticalOnly'
                                    ? eqSubcomponents.filter((sub) => isCriticalSubcomponentForPartsView(sub))
                                    : partListMode === 'criticalFirst'
                                      ? eqSubcomponents
                                        .slice()
                                        .sort(
                                          (left, right) =>
                                            Number(isCriticalSubcomponentForPartsView(right)) -
                                            Number(isCriticalSubcomponentForPartsView(left))
                                        )
                                      : eqSubcomponents
                                const criticalPartCount = criticalPartCountForEquipment(eqId)
                                const locationKey = normalizeLocationCode(eq.location)
                                const customIcon = locationKey ? equipmentIcons[locationKey] : undefined
                                const iconUrl = resolveIconUrl(customIcon?.iconUrl)

                                return (
                                  <div key={eqId} className='mb-3 position-relative'>
                                    {/* Equipment Row */}
                                    <div
                                      className='d-flex align-items-center p-2 rounded-3 bg-light-info border border-info border-opacity-50 shadow-sm'
                                      style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        background: 'linear-gradient(135deg, #e7f3ff 0%, #f0f8ff 100%)'
                                      }}
                                      onClick={() => handleToggleEquipment(eqId)}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateX(4px)'
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.15)'
                                        e.currentTarget.style.borderColor = '#0d6efd'
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateX(0)'
                                        e.currentTarget.style.boxShadow = ''
                                        e.currentTarget.style.borderColor = ''
                                      }}
                                    >
                                      <div
                                        className='rounded-circle p-1 bg-info bg-opacity-10 me-2 d-flex align-items-center justify-content-center'
                                        style={{ cursor: canManageEquipmentIcons ? 'pointer' : 'default' }}
                                        onClick={(e) => {
                                          if (!canManageEquipmentIcons) return
                                          e.stopPropagation()
                                          if (!locationKey) {
                                            toast.error('Location code is missing for this equipment', { position: 'top-center' })
                                            return
                                          }
                                          setIconModal({ open: true, locationCode: locationKey })
                                        }}
                                        title={
                                          canManageEquipmentIcons
                                            ? locationKey
                                              ? 'Change equipment icon'
                                              : 'Location code required to set icon'
                                            : undefined
                                        }
                                      >
                                        {iconUrl ? (
                                          <img
                                            src={iconUrl}
                                            alt='Equipment icon'
                                            style={{ width: 22, height: 22, objectFit: 'contain' }}
                                          />
                                        ) : (
                                          <KTSVG
                                            path='/media/icons/duotune/technology/teh005.svg'
                                            className='svg-icon-3 text-info'
                                          />
                                        )}
                                      </div>

                                      <div className='flex-grow-1'>
                                        <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
                                          <span className='fw-bold fs-7 text-dark d-flex align-items-center flex-wrap gap-2'>
                                            {eq.code && (
                                              <span className='badge bg-primary text-white px-2 py-1'>
                                                {eq.code}
                                              </span>
                                            )}
                                            <span>{eq.name}</span>
                                          </span>
                                          {eq.criticality && (
                                            <span className='badge bg-danger text-white px-2 py-1'>
                                              ⚠️ Critical
                                            </span>
                                          )}
                                          {defectEquipmentIds.has(eqId) && (
                                            <span className='badge bg-danger text-white px-2 py-1 d-flex align-items-center gap-1'>
                                              <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-4' />
                                              DEFECT
                                            </span>
                                          )}
                                        </div>
                                        {eq.subHead && (
                                          <div className='text-muted fs-8 mt-1'>
                                            {eq.subHead}
                                          </div>
                                        )}
                                        <div className='text-muted fs-8 d-flex flex-wrap gap-3 mt-1'>
                                          <span>Maker: {eq.maker || '-'}</span>
                                          <span>Critical: {eq.criticality ? 'Yes' : 'No/Not set'}</span>
                                        </div>
                                      </div>

                                      <div className='d-flex gap-2'>
                                        <button
                                          className='btn btn-sm btn-light shadow-sm'
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setViewDetail({ open: true, kind: 'equipment', data: eq })
                                          }}
                                          title='View Equipment'
                                          style={{ transition: 'all 0.2s ease' }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)'
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)'
                                          }}
                                        >
                                          <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-2' />
                                        </button>
                                        {canCreateSubcomponent && (
                                          <button
                                            className='btn btn-sm btn-success text-white fw-semibold shadow-sm'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setSubcomponentModal({ open: true, equipmentId: eqId })
                                            }}
                                            title='Add Subcomponent'
                                            style={{ transition: 'all 0.2s ease' }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'scale(1.05)'
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'scale(1)'
                                            }}
                                          >
                                            <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2 me-1' />
                                            Add Subcomponent
                                          </button>
                                        )}
                                        {canUpdateMachinery && (
                                          <button
                                            className='btn btn-sm btn-light-primary shadow-sm'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setEquipmentModal({ open: true, editing: eq })
                                            }}
                                            title='Edit Equipment'
                                            style={{ transition: 'all 0.2s ease' }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'scale(1.05)'
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'scale(1)'
                                            }}
                                          >
                                            <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-2' />
                                          </button>
                                        )}
                                        {canDeleteMachinery && (
                                          <button
                                            className='btn btn-sm btn-light-danger shadow-sm'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleDeleteEquipment(eqId)
                                            }}
                                            title='Delete Equipment'
                                            style={{ transition: 'all 0.2s ease' }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'scale(1.05)'
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'scale(1)'
                                            }}
                                          >
                                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2' />
                                          </button>
                                        )}
                                      </div>
                                    </div>


                                    {/* Subcomponents */}
                                    {isEqExpanded && (
                                      <div className='ms-5 mt-2 position-relative'>
                                        {canReadPart && eqSubcomponents.length > 0 && (
                                          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 p-2 rounded-3 border bg-white shadow-sm'>
                                            <div className='d-flex align-items-center gap-2 flex-wrap'>
                                              <span className='fw-semibold fs-8 text-dark'>Parts View</span>
                                              <span className='badge bg-light-danger text-danger'>
                                                {criticalPartCount} critical spare{criticalPartCount === 1 ? '' : 's'}
                                              </span>
                                            </div>
                                            <SearchableSelect
                                              className='form-select form-select-sm'
                                              value={partListMode}
                                              onChange={(e) =>
                                                setPartListMode(
                                                  e.target.value as 'all' | 'criticalOnly' | 'criticalFirst'
                                                )
                                              }
                                              style={{ maxWidth: '220px' }}
                                            >
                                              <option value='all'>All Parts</option>
                                              <option value='criticalOnly'>Critical Spares Only</option>
                                              <option value='criticalFirst'>Critical Spares First</option>
                                            </SearchableSelect>
                                          </div>
                                        )}

                                        {/* Connecting line */}
                                        <div
                                          className='position-absolute'
                                          style={{
                                            left: '-12px',
                                            top: 0,
                                            bottom: 0,
                                            width: '2px',
                                            background: 'linear-gradient(to bottom, #0dcaf0, #e0e0e0)',
                                            borderRadius: '2px',
                                          }}
                                        />
                                        {!canReadSubcomponent ? (
                                          <div className='text-muted p-3 fs-7 bg-light rounded-2 border border-dashed'>
                                            You do not have permission to view subcomponents.
                                          </div>
                                        ) : visibleEqSubcomponents.length === 0 ? (
                                          <div className='text-muted p-3 fs-7 bg-light rounded-2 border border-dashed'>
                                            {partListMode === 'criticalOnly'
                                              ? 'No critical spare parts found under this equipment.'
                                              : 'No subcomponents.'}
                                            {partListMode !== 'criticalOnly' && canCreateSubcomponent && (
                                              <> Click <span className='fw-semibold text-success'>Add Subcomponent</span> to create one.</>
                                            )}
                                          </div>
                                        ) : (
                                          visibleEqSubcomponents.map((sub) => {
                                            const subKey = getSubcomponentKey(sub)
                                            const subBaseId = sub.id
                                            const subParts = subBaseId ? partsForSubcomponent(subBaseId) : []
                                            const isSubExpanded = subKey ? expanded.subcomponents.has(subKey) : false
                                            return (
                                              <div key={subKey || sub.id} className='mb-2'>
                                                {/* Subcomponent Row - Clickable to expand/collapse */}
                                                <div
                                                  className='d-flex align-items-center p-2 rounded-3 bg-light-success border border-success border-opacity-50 shadow-sm'
                                                  style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    background: 'linear-gradient(135deg, #d1e7dd 0%, #d4edda 100%)',
                                                  }}
                                                  onClick={() => handleToggleSubcomponent(sub)}
                                                  onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(4px)'
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 135, 84, 0.15)'
                                                    e.currentTarget.style.borderColor = '#198754'
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(0)'
                                                    e.currentTarget.style.boxShadow = ''
                                                    e.currentTarget.style.borderColor = ''
                                                  }}
                                                >
                                                  {/* Expand/Collapse arrow for Subcomponent */}
                                                  <KTSVG
                                                    path={`/media/icons/duotune/arrows/arr${isSubExpanded ? '072' : '071'}.svg`}
                                                    className='svg-icon-2 me-2 text-success'
                                                  />

                                                  <div className='rounded-circle p-1 bg-success bg-opacity-10 me-2'>
                                                    <KTSVG
                                                      path='/media/icons/duotune/technology/teh001.svg'
                                                      className='svg-icon-2 text-success'
                                                    />
                                                  </div>
                                                  <div className='flex-grow-1'>
                                                    <div className='fw-bold fs-8 text-dark mb-1 d-flex align-items-center gap-2 flex-wrap'>
                                                      <span>{getSubcomponentDisplayName(sub)}</span>
                                                      {sub.criticality && (
                                                        <span className='badge bg-danger text-white px-2 py-1'>Critical</span>
                                                      )}
                                                      {(sub.instanceCount ?? 1) > 1 && (
                                                        <span className='badge bg-success text-white px-2 py-1'>
                                                          {sub.instanceCount}x
                                                        </span>
                                                      )}
                                                      {sub.hasOpenJob && (
                                                        <span className='badge badge-light-warning'>
                                                          <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-3 me-1' />
                                                          Overdue
                                                        </span>
                                                      )}
                                                      {subBaseId && defectSubcomponentIds.has(subBaseId) && (
                                                        <span className='badge bg-danger text-white px-2 py-1 d-flex align-items-center gap-1'>
                                                          <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-4' />
                                                          DEFECT
                                                        </span>
                                                      )}
                                                    </div>
                                                    {sub.subComponentCode && (
                                                      <div className='text-muted fs-8'>({sub.subComponentCode})</div>
                                                    )}
                                                    <div className='text-muted fs-8 d-flex flex-wrap gap-3 mt-1'>
                                                      <span>Critical: {sub.criticality ? 'Yes' : 'No/Not set'}</span>
                                                    </div>
                                                  </div>
                                                  <div className='d-flex gap-2'>
                                                    <button
                                                      className='btn btn-sm btn-light shadow-sm'
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        setViewDetail({ open: true, kind: 'subcomponent', data: sub })
                                                      }}
                                                      title='View Subcomponent'
                                                      style={{ transition: 'all 0.2s ease' }}
                                                      onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1.05)'
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1)'
                                                      }}
                                                    >
                                                      <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-2' />
                                                    </button>
                                                    {canCreatePart && (
                                                      <button
                                                        className='btn btn-sm btn-light-info shadow-sm d-flex align-items-center gap-1'
                                                        onClick={(e) => {
                                                          e.stopPropagation()
                                                          if (subBaseId) {
                                                            setPartModal({
                                                              open: true,
                                                              subcomponentId: subBaseId,
                                                              editing: undefined,
                                                              context: {
                                                                companyName: eq.companyName || (eq as any)?.company || '',
                                                                vesselName: (eq as any)?.vesselName || (eq as any)?.vessel?.fleet_name || '',
                                                                equipmentName: eq.name || eq.code || '',
                                                                subcomponentName: sub.name || '',
                                                                vesselId: (eq as any)?.vesselId,
                                                                equipmentId: eq.id,
                                                              }
                                                            })
                                                          }
                                                        }}
                                                        title='Add Part'
                                                        style={{ transition: 'all 0.2s ease' }}
                                                        onMouseEnter={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1.05)'
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1)'
                                                        }}
                                                      >
                                                        <KTSVG path='/media/icons/duotune/arrows/arr087.svg' className='svg-icon-2' />
                                                        <span>Add Part</span>
                                                      </button>
                                                    )}
                                                    {canUpdateSubcomponent && (
                                                      <button
                                                        className='btn btn-sm btn-light-primary shadow-sm'
                                                        onClick={(e) => {
                                                          e.stopPropagation()
                                                          setSubcomponentModal({
                                                            open: true,
                                                            editing: sub,
                                                            equipmentId: sub.equipmentId ?? eqId,
                                                          })
                                                        }}
                                                        title='Edit Subcomponent'
                                                        style={{ transition: 'all 0.2s ease' }}
                                                        onMouseEnter={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1.05)'
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1)'
                                                        }}
                                                      >
                                                        <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-2' />
                                                      </button>
                                                    )}
                                                    {canDeleteSubcomponent && (
                                                      <button
                                                        className='btn btn-sm btn-light-danger shadow-sm'
                                                        onClick={(e) => {
                                                          e.stopPropagation()
                                                          handleDeleteSubcomponent(sub.id!)
                                                        }}
                                                        title='Delete Subcomponent'
                                                        style={{ transition: 'all 0.2s ease' }}
                                                        onMouseEnter={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1.05)'
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.currentTarget.style.transform = 'scale(1)'
                                                        }}
                                                      >
                                                        <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2' />
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Parts under Subcomponent - Only show when expanded */}
                                                {isSubExpanded && (
                                                  <div className='ms-5 mt-2 position-relative'>
                                                    {/* Connecting line */}
                                                    <div
                                                      className='position-absolute'
                                                      style={{
                                                        left: '-12px',
                                                        top: 0,
                                                        bottom: 0,
                                                        width: '2px',
                                                        background: 'linear-gradient(to bottom, #6f42c1, #e0e0e0)',
                                                        borderRadius: '2px',
                                                      }}
                                                    />
                                                    {!canReadPart ? (
                                                      <div className='text-muted fs-7 mb-3 p-2 bg-light rounded-2 border border-dashed'>
                                                        You do not have permission to view parts.
                                                      </div>
                                                    ) : subParts.length > 0 ? (
                                                      subParts.map((part) => {
                                                        const longDesc = part.itemLongDescription
                                                        return (
                                                          <div
                                                            key={part.id}
                                                            className='d-flex align-items-center p-2 rounded-2 bg-white border border-purple border-opacity-25 mb-2 shadow-sm'
                                                            style={{
                                                              transition: 'all 0.2s ease',
                                                              borderColor: 'rgba(111, 66, 193, 0.25)',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                              e.currentTarget.style.transform = 'translateX(4px)'
                                                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(111, 66, 193, 0.15)'
                                                              e.currentTarget.style.borderColor = '#6f42c1'
                                                            }}
                                                            onMouseLeave={(e) => {
                                                              e.currentTarget.style.transform = 'translateX(0)'
                                                              e.currentTarget.style.boxShadow = ''
                                                              e.currentTarget.style.borderColor = 'rgba(111, 66, 193, 0.25)'
                                                            }}
                                                          >
                                                            <span
                                                              className='badge me-3 fw-bold text-white'
                                                              style={{
                                                                minWidth: 70,
                                                                textAlign: 'center',
                                                                fontSize: '0.7rem',
                                                                padding: '4px 6px',
                                                                backgroundColor: '#6f42c1',
                                                              }}
                                                            >
                                                              {part.drawNo ? part.drawNo : 'NO DRAW'}
                                                            </span>

                                                            <div
                                                              className='rounded-circle p-1 me-2 d-flex align-items-center justify-content-center'
                                                              style={{
                                                                backgroundColor: 'rgba(111, 66, 193, 0.1)',
                                                                color: '#6f42c1',
                                                              }}
                                                            >
                                                              <KTSVG
                                                                path='/media/icons/duotune/general/gen032.svg'
                                                                className='svg-icon-2'
                                                              />
                                                            </div>
                                                            <div className='flex-grow-1'>
                                                              <div className='fw-semibold fs-8 text-dark mb-1'>{part.name}</div>
                                                              <div className='text-muted fs-8 mb-1 d-flex align-items-center flex-wrap gap-2'>
                                                                {part.itemCode ? (
                                                                  <span className='badge bg-light text-dark'>{part.itemCode}</span>
                                                                ) : (
                                                                  <span className='badge bg-warning text-dark'>NO ITEM CODE</span>
                                                                )}
                                                                {(() => {
                                                                  const rawRob =
                                                                    part.robQuantity ?? part.rob ?? (part as any)?.currentQty
                                                                  const robValue = rawRob != null ? Number(rawRob) : 0
                                                                  const minValue = part.minThreshold ?? (part as any)?.minThreshold
                                                                  const isLow =
                                                                    minValue != null && Number(robValue) <= Number(minValue)
                                                                  const badgeClass = isLow
                                                                    ? 'bg-light-danger text-danger'
                                                                    : 'bg-light-info text-info'
                                                                  return (
                                                                    <span className={`badge ${badgeClass}`}>
                                                                      ROB {robValue?.toLocaleString?.() ?? robValue}
                                                                    </span>
                                                                  )
                                                                })()}
                                                                {isCriticalSparePart(part) && (
                                                                  <span className='badge bg-danger text-white'>CRITICAL</span>
                                                                )}
                                                                {longDesc ? (
                                                                  <span className='text-muted fs-8'>({longDesc})</span>
                                                                ) : null}
                                                              </div>
                                                              <div className='text-muted fs-8 d-flex flex-wrap gap-3 mt-1'>
                                                                <span className='d-flex align-items-center gap-1'>
                                                                  <span className='fw-semibold text-muted'>Drawing Position No:</span>
                                                                  <span>{part.drawingPositionNo ? part.drawingPositionNo : '—'}</span>
                                                                </span>
                                                                <span className='d-flex align-items-center gap-1'>
                                                                  <span className='fw-semibold text-muted'>Part Storage Location:</span>
                                                                  <span>{formatStorageLocation(part.location) === '-' ? '—' : formatStorageLocation(part.location)}</span>
                                                                </span>
                                                              </div>
                                                            </div>
                                                            <div className='d-flex gap-1'>
                                                              <button
                                                                className='btn btn-sm btn-light'
                                                                onClick={(e) => {
                                                                  e.stopPropagation()
                                                                  handleViewPart(part.id)
                                                                }}
                                                                title='View Part'
                                                                style={{ transition: 'all 0.2s ease' }}
                                                                onMouseEnter={(e) => {
                                                                  e.currentTarget.style.transform = 'scale(1.1)'
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                  e.currentTarget.style.transform = 'scale(1)'
                                                                }}
                                                              >
                                                                <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-2' />
                                                              </button>
                                                              {canUpdatePart && (
                                                                <button
                                                                  className='btn btn-sm btn-light-primary'
                                                                  onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    openEditPartModal(part, subBaseId, {
                                                                      companyName: eq.companyName || (eq as any)?.company || '',
                                                                      vesselName: (eq as any)?.vesselName || (eq as any)?.vessel?.fleet_name || '',
                                                                      equipmentName: eq.name || eq.code || '',
                                                                      subcomponentName: sub.name || '',
                                                                      vesselId: (eq as any)?.vesselId,
                                                                      equipmentId: eq.id,
                                                                    })
                                                                  }}
                                                                  title='Edit Part'
                                                                  style={{ transition: 'all 0.2s ease' }}
                                                                  onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1.1)'
                                                                  }}
                                                                  onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1)'
                                                                  }}
                                                                >
                                                                  <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-2' />
                                                                </button>
                                                              )}
                                                              <button
                                                                className='btn btn-sm btn-light'
                                                                onClick={(e) => {
                                                                  e.stopPropagation()
                                                                  setPartAttachmentsModal({ open: true, part })
                                                                }}
                                                                title='Attachments'
                                                                style={{ transition: 'all 0.2s ease' }}
                                                                onMouseEnter={(e) => {
                                                                  e.currentTarget.style.transform = 'scale(1.1)'
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                  e.currentTarget.style.transform = 'scale(1)'
                                                                }}
                                                              >
                                                                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2' />
                                                              </button>
                                                              {canDeletePart && (
                                                                <button
                                                                  className='btn btn-sm btn-light-danger'
                                                                  onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleDeletePart(part)
                                                                  }}
                                                                  title='Delete Part'
                                                                  style={{ transition: 'all 0.2s ease' }}
                                                                  onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1.1)'
                                                                  }}
                                                                  onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1)'
                                                                  }}
                                                                >
                                                                  <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2' />
                                                                </button>
                                                              )}
                                                            </div>
                                                          </div>
                                                        )
                                                      })
                                                    ) : partListMode === 'criticalOnly' ? (
                                                      <div className='text-muted fs-7 mb-3 p-2 bg-light rounded-2 border border-dashed'>
                                                        No critical spares found in this subcomponent.
                                                      </div>
                                                    ) : (
                                                      <div className='text-muted fs-7 mb-3 p-2 bg-light rounded-2 border border-dashed'>
                                                        No parts available.
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            )
                                          })
                                        )}
                                        {canCreateSubcomponent && (
                                          <button
                                            className='btn btn-sm btn-success text-white fw-semibold shadow-sm mt-3'
                                            onClick={() => setSubcomponentModal({ open: true, equipmentId: eqId })}
                                            style={{ transition: 'all 0.3s ease' }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'translateY(-2px)'
                                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(13, 202, 240, 0.3)'
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'translateY(0)'
                                              e.currentTarget.style.boxShadow = ''
                                            }}
                                          >
                                            <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2 me-2' />
                                            Add Subcomponent
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Equipment Modal */}
      {equipmentModal.open && (
        <EquipmentModal
          visible={true}
          vesselId={filters.vesselId ? Number(filters.vesselId) : undefined}
          editing={equipmentModal.editing}
          allEquipment={equipment}
          onClose={() => setEquipmentModal({ open: false })}
          onSubmit={equipmentModal.editing
            ? (data) => handleUpdateEquipment(equipmentModal.editing!.id!, data)
            : handleCreateEquipment}
        />
      )}
      {/* Subcomponent Modal */}
      {subcomponentModal.open && (
        <SubcomponentModal
          visible={true}
          equipmentId={subcomponentModal.equipmentId!}
          editing={subcomponentModal.editing}
          onClose={() => setSubcomponentModal({ open: false })}
          onSubmit={subcomponentModal.editing
            ? (data) => handleUpdateSubcomponent(subcomponentModal.editing!.id!, data)
            : handleCreateSubcomponent}
        />
      )}

      {iconModal.open && iconModal.locationCode && (
        <EquipmentIconModal
          visible={iconModal.open}
          locationCode={iconModal.locationCode}
          currentIcon={equipmentIcons[iconModal.locationCode] ?? null}
          onClose={() => setIconModal({ open: false })}
          onUpdated={(next) => {
            setEquipmentIcons((prev) => {
              const copy = { ...prev }
              const key = iconModal.locationCode!
              if (next) {
                copy[key] = {
                  ...next,
                  iconUrl: resolveIconUrl(next.iconUrl) || '',
                }
              } else {
                delete copy[key]
              }
              return copy
            })
          }}
        />
      )}

      {/* Part Modal */}
      {partModal.open && (
        <PartModal
          visible={true}
          subcomponentId={partModal.subcomponentId!}
          editing={partModal.editing}
          onClose={() => setPartModal({ open: false })}
          onSubmit={partModal.editing ? (data: PartDto) => handleUpdatePart(partModal.editing!.id!, data) : handleCreatePart}
          context={partModal.context}
        />
      )}
      {/* Part Details Modal */}
      {/* Part Attachments Modal */}
      {partAttachmentsModal.open && partAttachmentsModal.part && (
        <PartAttachmentsModal
          visible={true}
          part={partAttachmentsModal.part}
          onClose={() => setPartAttachmentsModal({ open: false, part: null })}
        />
      )}
      {/* Detail View Modal */}
      {viewDetail.open && (
        <div className='modal fade show d-block' tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title fw-bold'>
                  {viewDetail.kind === 'equipment' && 'Equipment Details'}
                  {viewDetail.kind === 'subcomponent' && 'Sub Component Details'}
                  {viewDetail.kind === 'part' && 'Part Details'}
                </h5>
                <button type='button' className='btn-close' onClick={() => setViewDetail({ open: false })}></button>
              </div>
              <div className='modal-body'>
                {viewDetail.kind === 'equipment' && viewDetail.data && (
                  <div className='row g-3'>
                    <div className='col-md-6'><strong>Name:</strong> {valueOrDash(viewDetail.data.name)}</div>
                    <div className='col-md-6'><strong>Code:</strong> {valueOrDash(viewDetail.data.code)}</div>
                    <div className='col-md-6'><strong>Short Form:</strong> {valueOrDash(viewDetail.data.shortForm)}</div>
                    <div className='col-md-6'><strong>Vessel:</strong> {valueOrDash((viewDetail.data as any).vesselName)}</div>
                    <div className='col-md-6'><strong>Company:</strong> {valueOrDash((viewDetail.data as any).companyName)}</div>
                    <div className='col-md-6'><strong>Criticality:</strong> {formatYesNo(viewDetail.data.criticality)}</div>
                    <div className='col-md-6'><strong>Maker:</strong> {valueOrDash(viewDetail.data.maker)}</div>
                    <div className='col-md-6'><strong>Model:</strong> {valueOrDash(viewDetail.data.model)}</div>
                    <div className='col-md-6'><strong>Serial Number:</strong> {valueOrDash(viewDetail.data.serialNumber)}</div>
                    <div className='col-md-6'><strong>Installation Date:</strong> {valueOrDash((viewDetail.data as any).installationDate)}</div>
                    <div className='col-md-6'><strong>Location:</strong> {valueOrDash(viewDetail.data.location)}</div>
                    <div className='col-md-6'><strong>Class Critical:</strong> {formatYesNo((viewDetail.data as any).isClassCritical)}</div>
                    <div className='col-md-6'><strong>Survey Relevant:</strong> {formatYesNo((viewDetail.data as any).isSurveyRelevant)}</div>
                    <div className='col-md-12'><strong>Function Description:</strong> {valueOrDash((viewDetail.data as any).equipmentFunctionDescription)}</div>
                    <div className='col-md-12'><strong>Equipment Particulars:</strong> {valueOrDash((viewDetail.data as any).equipmentParticulars)}</div>
                    <div className='col-md-12'><strong>Vessel Location Comments:</strong> {valueOrDash((viewDetail.data as any).vesselLocationComments)}</div>
                  </div>
                )}
                {viewDetail.kind === 'subcomponent' && viewDetail.data && (
                  <div className='row g-3'>
                    <div className='col-md-6'><strong>Name:</strong> {valueOrDash(viewDetail.data.name)}</div>
                    <div className='col-md-6'><strong>Code:</strong> {valueOrDash(viewDetail.data.subComponentCode)}</div>
                    <div className='col-md-6'><strong>Equipment Name:</strong> {valueOrDash((viewDetail.data as any).equipmentName)}</div>
                    <div className='col-md-6'><strong>Equipment Code:</strong> {valueOrDash((viewDetail.data as any).equipmentCode)}</div>
                    <div className='col-md-6'><strong>Vessel:</strong> {valueOrDash((viewDetail.data as any).vesselName)}</div>
                    <div className='col-md-6'><strong>Company:</strong> {valueOrDash((viewDetail.data as any).companyName)}</div>
                    <div className='col-md-6'><strong>Maker:</strong> {valueOrDash((viewDetail.data as any).maker)}</div>
                    <div className='col-md-6'><strong>Criticality:</strong> {formatYesNo(viewDetail.data.criticality)}</div>
                    <div className='col-md-6'><strong>Has Open Job:</strong> {formatYesNo((viewDetail.data as any).hasOpenJob)}</div>
                    <div className='col-md-6'><strong>Instance Count:</strong> {valueOrDash((viewDetail.data as any).instanceCount)}</div>
                    <div className='col-md-12'><strong>Function Description:</strong> {valueOrDash((viewDetail.data as any).subComponentFunctionDescription)}</div>
                    <div className='col-md-12'><strong>Particulars:</strong> {valueOrDash((viewDetail.data as any).subComponentParticulars)}</div>
                    <div className='col-md-6'><strong>Instance Number:</strong> {valueOrDash((viewDetail.data as any).subComponentInstanceNumber)}</div>
                    <div className='col-md-6'><strong>Instance Description:</strong> {valueOrDash((viewDetail.data as any).subComponentInstanceDescription)}</div>
                  </div>
                )}
                {viewDetail.kind === 'part' && viewDetail.data && (() => {
                  const partData = viewDetail.data as any
                  return (
                    <div className='row g-3'>
                      <div className='col-md-6'><strong>Part Name:</strong> {valueOrDash(partData.name)}</div>
                      <div className='col-md-6'><strong>Item Code:</strong> {valueOrDash(partData.itemCode || partData.code)}</div>

                      <div className='col-md-6'><strong>Sub Component Name:</strong> {valueOrDash(partData.subcomponent?.name)}</div>
                      <div className='col-md-6'><strong>Sub Component Code:</strong> {valueOrDash(partData.subcomponent?.subComponentCode)}</div>

                      <div className='col-md-6'><strong>Vessel:</strong> {valueOrDash(partData.vessel?.fleet_name || partData.vesselName)}</div>
                      <div className='col-md-6'><strong>Critical Spare:</strong> {formatYesNo(partData.criticalSpare)}</div>
                      <div className='col-md-6'>
                        <strong>Unit Of Measurement:</strong>{' '}
                        {valueOrDash(partData.unitOfMeasure || partData.uom?.code || partData.uom?.name)}
                      </div>

                      <div className='col-md-6'><strong>Manufacturer:</strong> {valueOrDash(partData.manufacturer)}</div>
                      <div className='col-md-6'><strong>Serial Number:</strong> {valueOrDash(partData.serialNumber)}</div>

                      <div className='col-md-6'><strong>Drawing No.:</strong> {valueOrDash(partData.drawNo)}</div>
                      <div className='col-md-6'><strong>Drawing Position No.:</strong> {valueOrDash(partData.drawingPositionNo)}</div>
                      <div className='col-md-6'><strong>Storage Location:</strong> {formatStorageLocation(partData.location)}</div>
                      <div className='col-md-6'><strong>ROB:</strong> {valueOrDash(partData.robQuantity ?? partData.rob ?? 0)}</div>
                      <div className='col-md-6'><strong>Min Threshold:</strong> {valueOrDash(partData.minThreshold)}</div>
                      <div className='col-md-6'><strong>Short Description:</strong> {valueOrDash(partData.itemShortDescription)}</div>

                      <div className='col-md-12'><strong>Long Description:</strong> {valueOrDash(partData.itemLongDescription)}</div>
                    </div>
                  )
                })()}
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={() => setViewDetail({ open: false })}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Storage Location Library Modal */}
      {storageLibraryOpen && canReadTechnicalLocationLibrary && (
        <PartStorageLocationLibraryModal
          visible={storageLibraryOpen}
          onClose={() => setStorageLibraryOpen(false)}
          canCreate={canCreateTechnicalLocationLibrary}
          canUpdate={canUpdateTechnicalLocationLibrary}
          canDelete={canDeleteTechnicalLocationLibrary}
        />
      )}
      {/* Machinery Upload Modal */}
      {canImportMachinery && (
        <MachineryUploadModal
          visible={uploadModal}
          onClose={() => setUploadModal(false)}
          onSuccess={() => {
            loadEquipment()
            loadVesselCounts()
          }}
        />
      )}
      {/* General Items Library Modal (opens from Machinery top-right button) */}
      {canReadGeneralItemLibrary && (
        <GeneralItemModal
          visible={showGeneralItemModal}
          onClose={() => setShowGeneralItemModal(false)}
          onSubmit={() => { }}
          vesselId={filters.vesselId ? Number(filters.vesselId) : undefined}
          allowRead={canReadGeneralItemLibrary}
          allowCreate={canCreateGeneralItemLibrary}
          allowUpdate={canUpdateGeneralItemLibrary}
          allowDelete={canDeleteGeneralItemLibrary}
          allowImport={canImportGeneralItemLibrary}
          allowExport={canExportGeneralItemLibrary}
        />
      )}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </div>
  )
}

export default MachineryDetails
